'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { getUpcomingMatchesByPlayer } from '@/models/eventModel';
import { createClient } from '@/lib/supabase/client';
import {
  fallbackCourtName,
  venueOfCourt,
  type EventVenueContext,
} from '@/app/[locale]/event/[eventId]/libs/venues';
import {
  courtDefinitionsOf,
  loadVenueContext,
} from '@/app/[locale]/event/[eventId]/libs/venuesLoad';
import { useFormatName } from '@/contexts/NameFormattingContext';
import type { MatchWithRating } from '@/components/match/types';
import type { Tables } from '@/types/supabase';

/**
 * Upcoming matches for the profile being viewed, mapped to the shared
 * `MatchWithRating` shape. Own profile only: `enabled=false` fetches nothing.
 */
export function useUpcomingMatches(player: Tables<'player'>, enabled: boolean) {
  const tDash = useTranslations('Dashboard');
  const formatName = useFormatName();
  const [matches, setMatches] = useState<MatchWithRating[]>([]);

  useEffect(() => {
    if (!enabled) return;
    let mounted = true;
    (async () => {
      try {
        const rows = await getUpcomingMatchesByPlayer(player.id);
        if (!mounted) return;
        // One venue context per distinct event (browser client; event_venue is
        // anon-readable). A failed load degrades to the event's own court
        // definitions with no venues, so the row still shows a court.
        const client = createClient();
        const ctxByEvent = new Map<string, EventVenueContext>();
        for (const match of rows) {
          const ev = match.event;
          if (ev?.id && !ctxByEvent.has(ev.id)) {
            ctxByEvent.set(
              ev.id,
              await loadVenueContext(client, ev.id).catch(() => ({
                venues: [],
                courts: courtDefinitionsOf(ev.settings),
              })),
            );
          }
        }
        if (!mounted) return;
        setMatches(
          rows.map((match) => {
            const isPlayer1 = match.player_1_id === player.id;
            const opponent = isPlayer1 ? match.player2 : match.player1;
            const event = match.event;
            const eventGroup = match.event_group;
            const location = event?.location;

            const ctx = (event?.id && ctxByEvent.get(event.id)) || {
              venues: [],
              courts: courtDefinitionsOf(event?.settings),
            };
            const venue = venueOfCourt(ctx, match.court_id);
            const court = ctx.courts.find((c) => c.id === match.court_id);
            const venueParts = [
              venue?.name ?? location?.name,
              match.court_id ? (court?.name ?? fallbackCourtName(match.court_id)) : null,
            ].filter(Boolean);

            return {
              id: match.id,
              dateISO: match.scheduled_datetime,
              opponent: opponent
                ? formatName(opponent.first_name, opponent.last_name)
                : tDash('tbd'),
              opponentUsername: opponent?.username || 'tbd',
              opponentCountry: opponent?.nationality || null,
              opponentProfilePhoto: opponent?.profile_photo || null,
              system: 'Event Match',
              result: 'upcoming',
              // Deliberate exception to joinVenueCourt (' · '): this hook's existing
              // ' - ' venue/court format is kept as-is.
              venue: venueParts.length > 0 ? venueParts.join(' - ') : tDash('tbd'),
              competition: event?.name || 'Event Match',
              competitionDivision: eventGroup?.name || null,
              winnerId: null,
              myRating: player.current_rating,
              myDelta: null,
              oppRating: opponent?.current_rating || null,
              oppDelta: null,
              isAdjustment: false,
            };
          }),
        );
      } catch {
        if (mounted) setMatches([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [enabled, player.id, player.current_rating, tDash, formatName]);

  return matches;
}
